import { Component } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from './PageMeta';
import { DataRPC } from '../../rpc-client/DataRPC';
import { s3 } from '../../config/constants';
import SakuraSep from './SakuraSep';
import SakuraOrbit from './SakuraOrbit';

const rpc = new DataRPC();

const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d + 'T00:00:00');
    const days = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
    return `${days[dt.getDay()]}, ${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};

const slotSortKey = (schedDate, startTime) =>
    `${schedDate || '9999-99-99'}_${startTime || '99:99'}`;

/* upcoming/ongoing = rank 0 (ascending), done = rank 1 (descending) */
const festStatusRank = (f) => {
    const now   = new Date(); now.setHours(0, 0, 0, 0);
    const start = new Date(f.start_date + 'T00:00:00');
    const end   = f.end_date ? new Date(f.end_date + 'T00:00:00') : start;
    return now > end ? 1 : 0;
};

class Schedule extends Component {
    state = {
        loading:           true,
        festivals:         [],
        activeFestId:      null,
        schedulesByFestId: {},
        schedLoading:      false,
        teamMap:           {},
        highlightKey:      null,
        festDropOpen:      false,
        searchQuery:       '',
        showOnlyMatches:   false,
    };

    teamKey = (slot) => slot.team_id ? String(slot.team_id) : ('__' + slot.team_name);

    toggleHighlight = (slot) => {
        const key = this.teamKey(slot);
        this.setState(prev => ({ highlightKey: prev.highlightKey === key ? null : key }));
    };

    slotMatchesSearch = (slot, query) => {
        const q = query.trim().toLowerCase();
        if (!q) return false;
        return (slot.team_name || '').toLowerCase().includes(q)
            || (slot.note || '').toLowerCase().includes(q);
    };

    async componentDidMount() {
        try {
            const currentYear = new Date().getFullYear();
            const [festRes, teamRes] = await Promise.all([
                rpc.fetchRawData('GetFestivals', { page: 1, limit: 100 }),
                rpc.fetchRawData('GetTeams',     { page: 1, limit: 200 }),
            ]);

            const teamMap = {};
            (teamRes.data || []).forEach(t => { teamMap[t.id] = { avatar: t.avatar_url, slug: t.slug }; });

            let all = (festRes.data || []);
            let festivals = all.filter(f => {
                const y = f.start_date ? new Date(f.start_date + 'T00:00:00').getFullYear() : null;
                return y === currentYear;
            });
            if (festivals.length === 0) festivals = all;

            festivals = festivals.slice().sort((a, b) => {
                const ra = festStatusRank(a), rb = festStatusRank(b);
                if (ra !== rb) return ra - rb;
                const dir = ra === 0 ? 1 : -1;
                return dir * (new Date(a.start_date) - new Date(b.start_date));
            });

            this.setState({ festivals, teamMap, loading: false }, () => {
                if (festivals.length > 0) {
                    this.loadFestivalSchedules(festivals[0].id);
                }
            });
        } catch {
            this.setState({ loading: false });
        }
    }

    loadFestivalSchedules = async (festId) => {
        /* already cached — just switch */
        if (this.state.schedulesByFestId[festId] !== undefined) {
            this.setState({ activeFestId: festId, festDropOpen: false, highlightKey: null, searchQuery: '', showOnlyMatches: false });
            return;
        }

        this.setState({ schedLoading: true, activeFestId: festId, festDropOpen: false, highlightKey: null, searchQuery: '', showOnlyMatches: false });
        try {
            const schedRes = await rpc.fetchRawData('GetSchedules', { festival_id: festId });
            const scheds   = schedRes || [];

            const withSlots = await Promise.all(
                scheds.map(async (s) => {
                    try {
                        const detail = await rpc.fetchRawData('GetSchedule1', { id: s.id });
                        return { ...s, slots: detail.slots || [] };
                    } catch {
                        return { ...s, slots: [] };
                    }
                })
            );

            withSlots.sort((a, b) => (a.schedule_date || '').localeCompare(b.schedule_date || ''));
            withSlots.forEach(sched => {
                sched.slots.sort((a, b) =>
                    slotSortKey(sched.schedule_date, a.start_time)
                        .localeCompare(slotSortKey(sched.schedule_date, b.start_time))
                );
            });

            this.setState(prev => ({
                schedulesByFestId: { ...prev.schedulesByFestId, [festId]: withSlots },
                schedLoading: false,
            }));
        } catch {
            this.setState({ schedLoading: false });
        }
    };

    render() {
        const {
            loading, festivals, activeFestId, schedulesByFestId,
            schedLoading, teamMap, highlightKey, festDropOpen,
            searchQuery, showOnlyMatches,
        } = this.state;

        const activeFest    = festivals.find(f => f.id === activeFestId);
        const activeIdx     = festivals.findIndex(f => f.id === activeFestId);
        const schedules     = schedulesByFestId[activeFestId] || [];
        const isSearching   = searchQuery.trim().length > 0;
        const visibleSchedules = (isSearching && showOnlyMatches)
            ? schedules
                .map(sched => ({ ...sched, slots: sched.slots.filter(slot => this.slotMatchesSearch(slot, searchQuery)) }))
                .filter(sched => sched.slots.length > 0)
            : schedules;

        return (
            <div className="sch-page">
                <PageMeta title="Lịch biểu diễn" description="Xem lịch biểu diễn Yosakoi – thời gian, địa điểm và các đội tham gia." url="/schedule" />

                {/* Hero */}
                <div className="sch-hero">
                    <div className="sch-hero-overlay" />
                    <div className="sch-hero-content">
                        <div className="entry-meta-date">Việt Nam Yosakoi Festival</div>
                        <h1 className="page-header-title">LỊCH DIỄN</h1>
        {/*                 <p className="sch-hero-sub">Lịch diễn của các đội</p> */}
                    </div>
                    <div className="sch-hero-kanji" aria-hidden="true">演</div>
                </div>

                <div className="sep-gold-rule" style={{margin: 0}} />

                <div className="sch-body">
                    <div className="entry-title">
                        <p>LỊCH DIỄN</p>
                        <h2>LỊCH TRÌNH BIỂU DIỄN</h2>
                    </div>

                    {loading ? (
                        /* ── Initial page loading ── */
                        <div className="sch-loading">
                            <SakuraOrbit label="Đang tải lịch diễn" />
                            {[0, 1, 2].map(i => (
                                <div key={i} className="sch-sk-slot" style={{ animationDelay: `${i * 0.12}s` }}>
                                    <div className="sch-sk-rail">
                                        <div className="sch-sk-rail-dot" />
                                        {i < 2 && <div className="sch-sk-rail-line" />}
                                    </div>
                                    <div className="sch-sk-card">
                                        <div className="sch-sk-idx" />
                                        <div className="sch-sk-avatar" />
                                        <div className="sch-sk-body">
                                            <div className="sch-sk-name" />
                                            <div className="sch-sk-note" />
                                        </div>
                                        <div className="sch-sk-time" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : festivals.length === 0 ? (
                        <div className="sch-empty">
                            <span className="sch-empty-kanji">待</span>
                            <p>Chưa có lịch biểu diễn nào được công bố.</p>
                            <p className="sch-empty-sub">Hãy theo dõi để cập nhật sớm nhất!</p>
                        </div>
                    ) : (
                        <>
                            {/* Festival picker dropdown */}
                            <div className="sch-fest-drop-wrap">
                                <button
                                    className={`sch-fest-drop-btn${festDropOpen ? ' open' : ''}`}
                                    onClick={() => this.setState(p => ({ festDropOpen: !p.festDropOpen }))}
                                >
                                    <span className="sch-fest-drop-icon">⛩</span>
                                    <span className="sch-fest-drop-label">{activeFest ? activeFest.name : 'Chọn festival'}</span>
                                    <span className="sch-fest-drop-meta">{activeIdx + 1} / {festivals.length}</span>
                                    <span className={`sch-fest-drop-chevron${festDropOpen ? ' up' : ''}`}>▾</span>
                                </button>

                                {festDropOpen && (
                                    <>
                                        <div className="sch-fest-drop-backdrop" onClick={() => this.setState({ festDropOpen: false })} />
                                        <div className="sch-fest-drop-panel">
                                            {festivals.map((f) => {
                                                const cached = schedulesByFestId[f.id];
                                                return (
                                                    <button
                                                        key={f.id}
                                                        className={`sch-fest-drop-item${f.id === activeFestId ? ' active' : ''}`}
                                                        onClick={() => this.loadFestivalSchedules(f.id)}
                                                    >
                                                        <span className="sch-fest-drop-item-dot" />
                                                        <span className="sch-fest-drop-item-name">{f.name}</span>
                                                        <span className="sch-fest-drop-item-count">
                                                            {cached !== undefined ? `${cached.length} buổi` : '—'}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Team search */}
                            <div className="sch-search-bar">
                                <div className="sch-search-input-wrap">
                                    <svg className="sch-search-icon" viewBox="0 0 20 20" fill="none">
                                        <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6"/>
                                        <path d="M13 13l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                    </svg>
                                    <input
                                        type="text"
                                        className="sch-search-input"
                                        placeholder="Tìm đội theo tên hoặc ghi chú..."
                                        value={searchQuery}
                                        onChange={e => this.setState({ searchQuery: e.target.value })}
                                    />
                                    {searchQuery && (
                                        <button type="button" className="sch-search-clear" onClick={() => this.setState({ searchQuery: '' })}>✕</button>
                                    )}
                                </div>
                            </div>

                            {/* Schedule content */}
                            {schedLoading ? (
                                <div className="sch-loading">
                                    <SakuraOrbit label="Đang tải lịch diễn" />
                                    {[0, 1, 2].map(i => (
                                        <div key={i} className="sch-sk-slot" style={{ animationDelay: `${i * 0.12}s` }}>
                                            <div className="sch-sk-rail">
                                                <div className="sch-sk-rail-dot" />
                                                {i < 2 && <div className="sch-sk-rail-line" />}
                                            </div>
                                            <div className="sch-sk-card">
                                                <div className="sch-sk-idx" />
                                                <div className="sch-sk-avatar" />
                                                <div className="sch-sk-body">
                                                    <div className="sch-sk-name" />
                                                    <div className="sch-sk-note" />
                                                </div>
                                                <div className="sch-sk-time" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : schedules.length === 0 ? (
                                <div className="sch-empty">
                                    <span className="sch-empty-kanji">待</span>
                                    <p>Chưa có lịch biểu diễn nào được công bố.</p>
                                    <p className="sch-empty-sub">Hãy theo dõi để cập nhật sớm nhất!</p>
                                </div>
                            ) : visibleSchedules.length === 0 ? (
                                <div className="sch-empty">
                                    <span className="sch-empty-kanji">検</span>
                                    <p>Không tìm thấy đội nào khớp với "{searchQuery.trim()}".</p>
                                    <p className="sch-empty-sub">Thử từ khoá khác hoặc bỏ chọn "Chỉ hiện đội tìm thấy".</p>
                                </div>
                            ) : (
                                <div className="sch-content">
                                    {visibleSchedules.map((sched) => (
                                        <div key={sched.id} className="sch-day-block">
                                            {/* Day header */}
                                            <div className="sch-day-header">
                                                <div className="sch-day-icon">🗓</div>
                                                <div className="sch-day-info">
                                                    <span className="sch-day-name">{sched.name}</span>
                                                    {sched.schedule_date && (
                                                        <span className="sch-day-date">{fmtDate(sched.schedule_date)}</span>
                                                    )}
                                                </div>
                                                <div className="sch-day-count">{sched.slots.length} tiết mục</div>
                                            </div>

                                            {/* Timeline */}
                                            {sched.slots.length === 0 ? (
                                                <div className="sch-no-slots">Chưa có tiết mục nào</div>
                                            ) : (
                                                <div className="sch-timeline">
                                                    {sched.slots.map((slot, idx) => {
                                                        const slotKey = this.teamKey(slot);
                                                        const isMatch = isSearching && this.slotMatchesSearch(slot, searchQuery);
                                                        const isLit   = isSearching ? isMatch : highlightKey === slotKey;
                                                        const isDim   = isSearching ? !isMatch : (highlightKey !== null && !isLit);
                                                        const team    = slot.team_id ? teamMap[slot.team_id] : null;
                                                        const avatarInner = team && team.avatar
                                                            ? <img src={s3(team.avatar, '')} alt={slot.team_name} />
                                                            : <span className="sch-slot-avatar-fallback">{(slot.team_name || '?')[0].toUpperCase()}</span>;
                                                        return (
                                                            <div key={idx} className="sch-slot">
                                                                <div className="sch-slot-rail">
                                                                    <div className={`sch-slot-dot${isLit ? ' sch-slot-dot--lit' : ''}`} />
                                                                    {idx < sched.slots.length - 1 && <div className="sch-slot-line" />}
                                                                </div>

                                                                <div
                                                                    className={`sch-slot-card${isLit ? ' sch-slot-card--lit' : ''}${isDim ? ' sch-slot-card--dim' : ''}`}
                                                                    onClick={() => this.toggleHighlight(slot)}
                                                                >
                                                                    {isLit && <div className="sch-slot-sparkle" aria-hidden="true"><span>✦</span><span>✦</span><span>✦</span></div>}

                                                                    <div className={`sch-slot-idx${isLit ? ' sch-slot-idx--lit' : ''}`}>{idx + 1}</div>

                                                                    <div className="sch-slot-avatar-wrap">
                                                                        <div className="sch-slot-avatar">
                                                                            {team
                                                                                ? <Link
                                                                                    className="sch-slot-avatar-link"
                                                                                    to={`/teams/${team.slug || slot.team_id}`}
                                                                                    onClick={e => e.stopPropagation()}
                                                                                  >
                                                                                    {avatarInner}
                                                                                  </Link>
                                                                                : avatarInner
                                                                            }
                                                                        </div>
                                                                        {team && (
                                                                            <span className="sch-slot-avatar-tip">
                                                                                <span className="sch-slot-avatar-tip-cta">Xem trang đội →</span>
                                                                                <span className="sch-slot-avatar-tip-name">{slot.team_name}</span>
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    <div className="sch-slot-body">
                                                                        <span className="sch-slot-team">{slot.team_name || '—'}</span>
                                                                        {slot.note && (
                                                                            <span className="sch-slot-note">{slot.note}</span>
                                                                        )}
                                                                    </div>

                                                                    <div className="sch-slot-time">
                                                                        {slot.start_time && (
                                                                            <span className="sch-slot-time-pill">
                                                                                <span className="sch-slot-time-icon">⏱</span>
                                                                                {slot.start_time}
                                                                                {slot.end_time && <> → {slot.end_time}</>}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <SakuraSep />

                <div className="sep-gold-rule" style={{margin: 0}} />
            </div>
        );
    }
}

export default Schedule;
